'use client';

import { useState } from 'react';
import { Loader2, Lock, LogIn, MailCheck, Send, ShieldCheck, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LoginForm from './LoginForm';
import { solicitarRecuperacion, type Sesion } from '@/lib/auth';

// Columna derecha de la tarjeta de ingreso (diseño de referencia Login.png):
// encabezado "Iniciar Sesión" en verde institucional + formulario. Alterna con
// el flujo de "Recuperar contraseña" (auto-servicio: el backend envía al correo
// registrado el enlace de restablecimiento).
export default function LoginCard({ onLogin }: { onLogin: (s: Sesion) => void }) {
  const [modo, setModo] = useState<'login' | 'recuperar'>('login');

  if (modo === 'recuperar') {
    return (
      <div className="mx-auto w-full max-w-sm space-y-5">
        <div className="text-center">
          <h2 className="flex items-center justify-center gap-2 text-2xl font-bold tracking-tight text-primary">
            <Lock className="size-6" /> Recuperar contraseña
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Ingrese su número de documento y le enviaremos al correo registrado un
            enlace para restablecerla.
          </p>
        </div>
        <FormRecuperar onVolver={() => setModo('login')} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-sm">
      <div className="text-center">
        <h2 className="flex items-center justify-center gap-2.5 text-3xl font-bold tracking-tight text-primary">
          <LogIn className="size-7" /> Iniciar Sesión
        </h2>
        <p className="mt-1.5 text-sm text-slate-500">Ingrese su documento y contraseña</p>
      </div>

      <div className="mt-6">
        <LoginForm onLogin={onLogin} onRecuperar={() => setModo('recuperar')} />
      </div>

      <p className="mt-6 flex items-center justify-center gap-1.5 border-t border-slate-200 pt-4 text-[11px] text-slate-400">
        <ShieldCheck className="size-3.5" />
        Autenticación institucional · Información protegida
      </p>
    </div>
  );
}

// Formulario de recuperación: documento → solicitud pública → mensaje genérico
// (no confirma ni niega si el usuario existe, para no exponer datos).
function FormRecuperar({ onVolver }: { onVolver: () => void }) {
  const [documento, setDocumento] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!documento.trim()) return;
    setEnviando(true);
    try {
      await solicitarRecuperacion(documento.trim());
      setEnviado(true);
    } catch {
      // Incluso ante un error de red se muestra el mensaje genérico al volver a intentar.
    } finally {
      setEnviando(false);
    }
  }

  if (enviado) {
    return (
      <div className="space-y-4 text-center">
        <MailCheck className="mx-auto size-10 text-primary" />
        <p className="text-sm text-slate-600">
          Si el documento <strong className="text-slate-900">{documento.trim()}</strong>{' '}
          corresponde a un usuario activo, enviamos un enlace de restablecimiento al
          correo registrado. Revise su bandeja de entrada (y el correo no deseado).
        </p>
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-xl border-slate-200 transition-colors duration-150 hover:bg-slate-50"
          onClick={onVolver}
        >
          Volver al ingreso
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={enviar} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="doc-recuperar" className="font-semibold text-primary">
          Número de Documento
        </Label>
        <div className="relative">
          <User className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-primary" />
          <Input
            id="doc-recuperar"
            type="text"
            inputMode="numeric"
            autoComplete="username"
            placeholder="Ej: 123456789"
            className="h-12 rounded-xl border-2 border-primary/45 bg-white pl-11 text-[15px] text-slate-900 shadow-sm transition-colors duration-200 placeholder:text-primary/45 hover:border-primary/70 focus-visible:border-primary focus-visible:ring-primary/20"
            value={documento}
            onChange={(e) => setDocumento(e.target.value.replace(/\D/g, ''))}
          />
        </div>
      </div>
      <Button
        type="submit"
        className="h-12 w-full rounded-xl bg-gradient-to-r from-[#15803d] to-[#166534] text-[15px] font-semibold text-white shadow-lg shadow-primary/25 transition-all duration-200 hover:from-[#166534] hover:to-[#14532d] hover:shadow-xl active:scale-[0.99]"
        disabled={enviando || !documento.trim()}
      >
        {enviando ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        {enviando ? 'Enviando…' : 'Enviar enlace de recuperación'}
      </Button>
      <Button
        type="button"
        variant="outline"
        className="w-full rounded-xl border-slate-200 transition-colors duration-150 hover:bg-slate-50"
        onClick={onVolver}
      >
        Volver al ingreso
      </Button>
    </form>
  );
}
